import clsx from 'clsx';
import {
  GroupScoreIndicatorAwareness,
  ScoreHealthRangeBreakpointLabels,
  ScoreHealthRangeContainer,
  ScoreHealthRangeGraphic,
  ScoreHealthRangeGroup,
  ScoreHealthRangeGroupingContainer,
  ScoreHealthRangeScoreIndicator,
  useScoreHealthRangeValueItemPositioning,
} from '..';
import { motion } from 'framer-motion';
import { useFirmClient } from '../../../FirmClient';
import { isScoreWithinBreakpoint } from '../ScoreHealthRange/util/isScoreWithinBreakpoint';
import { calculateAgeInYears, findHeadOfHouseholdWithAge, usePersons } from '../../../Person';
import { FinancialCategory } from '../../../generated/graphql';
import { useCategoryBreakpointsByAge } from './hooks/useCategoryBreakpointsByAge';

export interface HouseholdHealthScoreRangeProps {
  firmID: string;
  className?: string;
  householdID: string;
  firmClientID: string;
}

// eslint-disable-next-line noCommentedCode/no-commented-code
/**
 *
 * Renders a visual indicator of a household's financial health score within age-adjusted category breakpoints.
 *
 * Displays an animated marker and score indicator positioned according to the household's financial category score, with labels and groupings based on breakpoints tailored to the head of household's age. Returns `null` if the household's financial categorization is unknown and data has finished loading.
 *
 * 
 * @param className - Optional CSS class name for styling.
 * @param firmClientID - Unique identifier for the firm client.
 * @param firmID - Unique identifier for the firm.
 * @param householdID - Unique identifier for the household whose score is displayed.
 *
 * @returns A React element visualizing the household's financial health score, or `null` if the categorization is unknown.
 *
 */
export function HouseholdHealthScoreRange({
  className,
  firmClientID,
  firmID,
  householdID,
}: HouseholdHealthScoreRangeProps) {
  const { persons } = usePersons({ householdID });
  const { firmClient, firmClientQuery } = useFirmClient({ firmClientID, firmID });
  const breakpts = useCategoryBreakpointsByAge(calculateAgeInYears(findHeadOfHouseholdWithAge(persons)?.dateOfBirth));

  const financialCategoryScore = Math.round((firmClient?.household?.financialCategoryScore || 0) * 10);
  const { leftStyle } = useScoreHealthRangeValueItemPositioning({
    numberOfSections: 5,
    breakpoints: breakpts,
    rangeValue: financialCategoryScore,
  });

  if (firmClient?.household?.financialCategorization === FinancialCategory.Unknown && firmClientQuery.isSuccess) {
    return null;
  }

  return (
    <ScoreHealthRangeContainer className={className}>
      <ScoreHealthRangeBreakpointLabels displayBreakpoints={breakpts.slice(1).map(({ min }) => min.toString())} />
      <ScoreHealthRangeGraphic
        numberOfSections={5}
        targetScoreRangeGraphic={
          <motion.div
            layout="position"
            style={{ left: `calc(${leftStyle} - 4px)` }}
            className="h-2 w-2 bg-palette-600 rounded-full absolute z-10 pointer-events-none inset-y-0 m-auto min-w-2"
          />
        }
        scoreHealthRangeIndicator={
          <ScoreHealthRangeScoreIndicator
            leftStyle={leftStyle}
            isLoading={firmClientQuery.isLoading}
            scoreDisplay={financialCategoryScore.toString()}
          />
        }
        className={clsx('[&_.ScoreHealthRangeGraphic-Line]:!bg-palette-600/20')}
      />
      <ScoreHealthRangeGroupingContainer>
        {breakpts.map((breakpoint) => (
          <GroupScoreIndicatorAwareness
            key={breakpoint.id}
            isScoreWithinBreakpoint={isScoreWithinBreakpoint({
              breakpoint,
              breakpoints: breakpts,
              rangeValue: financialCategoryScore,
            })}
          >
            <ScoreHealthRangeGroup
              role="none"
              className="pointer-events-none [&_.ScoreHealthRangeGroup-label]:!lowercase"
              groupDisplay={breakpoint.categoryDisplay}
            />
          </GroupScoreIndicatorAwareness>
        ))}
      </ScoreHealthRangeGroupingContainer>
    </ScoreHealthRangeContainer>
  );
}
